/**
 * StructuredInfoBlock — auto-generates a clean Important Dates & Fees table
 * plus a step-by-step "How to Apply" guide for every job / admit-card / result page.
 *
 * Designed for SEO: pure semantic HTML <table>, <ol>, <li>, <h2>, <h3>.
 * No client-side conditional content — every post gets the same structured
 * layout so Google sees a consistent template.
 */
import { fmtDate } from "@/lib/api";

const SITE = "https://hireds.in";

function HowToApplySteps({ kind, item }) {
  const title = item.title || "this notification";
  const org = item.organization || "the recruiting body";
  const link =
    item.apply_link ||
    item.notification_link ||
    item.download_link ||
    item.result_link ||
    "";
  const last = item.last_date ? fmtDate(item.last_date) : null;

  // Step text shared across all 3 kinds, with conditional variants.
  const baseSteps = [
    {
      h: "Read the official notification PDF first",
      p: `Before filling any form, download and read the full notification published by ${org}. It carries the binding eligibility criteria, age-relaxation table, fee structure, exam pattern, and the syllabus. The detailed page above is a clean summary — the PDF is the legal document.`,
    },
    {
      h: "Confirm you meet eligibility",
      p: `Check your age (as on the cut-off date stated in the notification), educational qualification, and category-specific reservations. Many candidates fill the form and lose the fee because their qualification did not match exactly.`,
    },
    {
      h: "Keep documents ready before opening the form",
      p: "Scanned passport-size photo (recent, white background), signature, left-thumb impression, 10th certificate, qualifying degree, caste/EWS/PwD certificate if applicable, and a valid photo ID (Aadhaar / PAN / Voter ID). Use 100% scale and the file-size limits the portal specifies.",
    },
    {
      h: `Open the official application portal`,
      p: link
        ? `Use only the official application URL: <a href="${link}" target="_blank" rel="noopener noreferrer nofollow" class="text-orange-600 underline">${link.replace(/^https?:\/\//, "")}</a>. Never apply through a Telegram / WhatsApp forwarded link — those are scams.`
        : `Find the official application portal of ${org} (a .gov.in / .nic.in / .in domain). Never apply through a Telegram / WhatsApp forwarded link.`,
    },
    {
      h: "Register, then log in",
      p: "Most portals require a one-time registration with your name, mobile, email and a password. Save the registration number — you will need it for every future login, admit-card download and result check.",
    },
    {
      h: "Fill the form carefully",
      p: "Personal details, educational qualification, communication address, category, and preferences. Cross-check each field. After final submission, most portals lock the form and corrections cost an extra fee.",
    },
    {
      h: "Pay the application fee online",
      p: "Use UPI, net-banking, debit / credit card. Keep the transaction receipt; some portals require you to upload the receipt before downloading the admit card.",
    },
    {
      h: "Submit & save the confirmation",
      p: last
        ? `Submit the form well before ${last}. Save the final PDF acknowledgement and the registration number. Last-day submissions frequently fail because of portal overload.`
        : "Save the final PDF acknowledgement and your registration number. Last-day submissions frequently fail because of portal overload — do not wait.",
    },
  ];

  // Admit-card flow is download-focused, so override the last few steps.
  const admitSteps = [
    {
      h: "Visit the official admit-card download portal",
      p: link
        ? `Open <a href="${link}" target="_blank" rel="noopener noreferrer nofollow" class="text-orange-600 underline">${link.replace(/^https?:\/\//, "")}</a> on a desktop browser for the cleanest download.`
        : `Go to the official portal of ${org} on a desktop browser for the cleanest download.`,
    },
    {
      h: "Log in with registration number and date of birth",
      p: "Your registration number was issued when you filled the application form. If you have lost it, most portals offer a 'Forgot Registration No.' recovery using your name + date of birth + registered email.",
    },
    {
      h: "Verify your photograph, signature and exam centre",
      p: "Check the photo is recognisable, your name matches your photo ID, and the exam centre / shift timings are clearly printed. Mismatches must be reported to the helpline well before exam day.",
    },
    {
      h: "Download and take two colour printouts",
      p: "A4 size, single side. Carry one to the exam centre and keep the second copy at home as a backup. A faint or photocopy print can lead to entry refusal.",
    },
    {
      h: "Carry a valid photo ID",
      p: "Aadhaar / PAN / Voter ID / Driving Licence / Passport (in original — not photocopy). Without a matching photo ID, you will not be allowed into the exam hall.",
    },
  ];

  const resultSteps = [
    {
      h: "Visit the official result portal",
      p: link
        ? `Open <a href="${link}" target="_blank" rel="noopener noreferrer nofollow" class="text-orange-600 underline">${link.replace(/^https?:\/\//, "")}</a> directly. Result pages get heavy traffic on declaration day; try off-peak hours if it does not load.`
        : `Go to the official result page of ${org}. Result pages get heavy traffic on declaration day; try off-peak hours if it does not load.`,
    },
    {
      h: "Click the result link for your exam",
      p: `Look for the link labelled "${item.exam_name || title}" Result. Most portals open a PDF with the list of qualified roll numbers or a login form to check your individual score.`,
    },
    {
      h: "Enter your roll number / registration number",
      p: "Use the same registration number printed on your admit card. Your individual scorecard will show your section-wise marks, total score, and category-wise rank.",
    },
    {
      h: "Cross-check against the cutoff",
      p: "Compare your total score against the category-wise cutoff PDF. If you qualify, the next stage (mains / interview / DV) will be notified separately. If you miss the cutoff narrowly, request the scorecard re-verification within the deadline.",
    },
    {
      h: "Save and print your scorecard",
      p: "Download the scorecard PDF and keep two printouts. You will need it for next-stage admission, document verification, and as evidence if any discrepancy is raised later.",
    },
  ];

  const steps =
    kind === "admit-card"
      ? admitSteps
      : kind === "result"
      ? resultSteps
      : baseSteps;

  return (
    <ol
      className="list-decimal list-outside ml-5 space-y-4 text-slate-700 text-sm md:text-base leading-relaxed"
      data-testid="structured-how-to-apply-steps"
    >
      {steps.map((s, i) => (
        <li key={i} className="pl-1">
          <h3 className="font-bold text-slate-900 inline">{s.h}.</h3>{" "}
          <span dangerouslySetInnerHTML={{ __html: s.p }} />
        </li>
      ))}
    </ol>
  );
}

function DatesAndFeesTable({ kind, item }) {
  const isAdmit = kind === "admit-card";
  const isResult = kind === "result";
  const isJob = kind === "job";

  const rows = [];

  if (isJob) {
    rows.push(
      ["Notification Released", item.notification_date ? fmtDate(item.notification_date) : "See official PDF"],
      ["Application Start Date", item.notification_date ? fmtDate(item.notification_date) : "Refer notification"],
      ["Last Date to Apply", item.last_date ? fmtDate(item.last_date) : "Refer notification"],
      ["Last Date for Fee Payment", item.last_date ? fmtDate(item.last_date) : "Same as last date"],
      ["Total Vacancies", item.total_posts ? `${item.total_posts} posts` : "As per notification"],
      ["Application Fee", item.application_fee || "General / OBC: ₹100–₹500. SC / ST / PwD / Women: Usually exempt. Refer notification for exact amount."],
      ["Mode of Payment", "Online — UPI / Net Banking / Debit Card / Credit Card"],
      ["Mode of Application", "Online only via the official portal listed above"],
    );
  } else if (isAdmit) {
    rows.push(
      ["Exam Name", item.exam_name || item.title],
      ["Conducting Body", item.organization || "—"],
      ["Admit Card Released On", item.release_date ? fmtDate(item.release_date) : "See official portal"],
      ["Exam Date", item.exam_date ? fmtDate(item.exam_date) : "Refer admit card"],
      ["Mode of Download", "Online only — official portal"],
      ["Documents to Carry", "Printed admit card + original photo ID (Aadhaar / PAN / Voter ID / Driving Licence / Passport)"],
      ["Application Fee Status", "Already paid at the application stage — admit-card download is free"],
    );
  } else if (isResult) {
    rows.push(
      ["Exam Name", item.exam_name || item.title],
      ["Conducting Body", item.organization || "—"],
      ["Result Declared On", item.result_date ? fmtDate(item.result_date) : "See official portal"],
      ["Mode of Result Check", "Online only — official portal"],
      ["Documents Needed", "Registration number / Roll number + Date of birth"],
      ["Scorecard Validity", "Permanent record — download and preserve as PDF"],
      ["Re-verification Window", "Usually 7–15 days from declaration — refer notification"],
    );
  }

  return (
    <div className="overflow-x-auto" data-testid="structured-dates-fees-table">
      <table className="w-full text-sm md:text-base border-collapse">
        <caption className="sr-only">
          Important dates and fee structure for {item.title}
        </caption>
        <thead>
          <tr className="bg-slate-100 text-left">
            <th scope="col" className="px-4 py-3 font-bold text-slate-900 border border-slate-200 w-1/2 md:w-1/3">
              Field
            </th>
            <th scope="col" className="px-4 py-3 font-bold text-slate-900 border border-slate-200">
              Detail
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} className="even:bg-slate-50">
              <th scope="row" className="px-4 py-3 font-semibold text-slate-700 border border-slate-200 text-left align-top">
                {k}
              </th>
              <td className="px-4 py-3 text-slate-700 border border-slate-200 align-top">
                {v}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function StructuredInfoBlock({ kind, item }) {
  if (!item || typeof item !== "object") return null;

  const headingByKind = {
    job: "Important Dates, Fees & How to Apply",
    "admit-card": "Important Dates & How to Download the Admit Card",
    result: "Important Dates & How to Check the Result",
  };
  const heading = headingByKind[kind] || "Important Dates & Process";

  const applyHeading =
    kind === "admit-card"
      ? "Step-by-step: How to download the admit card"
      : kind === "result"
      ? "Step-by-step: How to check your result"
      : "Step-by-step: How to apply online";

  return (
    <section
      className="mt-8 bg-white border border-slate-200 rounded-xl p-5 md:p-7"
      aria-labelledby="structured-info-heading"
      data-testid={`structured-info-block-${kind}`}
    >
      <h2
        id="structured-info-heading"
        className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 mb-2"
        style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}
      >
        {heading}
      </h2>
      <p className="text-sm text-slate-600 mb-5">
        A structured summary auto-generated for every {kind.replace("-", " ")} on hireds.in. For binding rules, always cross-check the official notification linked above.
      </p>

      <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
        Important Dates &amp; Fees
      </h3>
      <DatesAndFeesTable kind={kind} item={item} />

      <h3 className="mt-8 text-lg md:text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
        {applyHeading}
      </h3>
      <HowToApplySteps kind={kind} item={item} />

      <p className="mt-6 text-xs text-slate-500 border-t border-slate-200 pt-4">
        Source verified by the hireds.in editorial team. For the official notification, always visit the recruiter portal listed at the top of this page.{" "}
        <a href={`${SITE}/editorial-policy`} className="text-orange-600 hover:underline">Editorial policy</a>.
      </p>
    </section>
  );
}
